import React, { Fragment, useEffect, useState } from 'react';
import API from '../../utils/ApiConfig';
import { Column } from 'primereact/column';
import { Divider } from 'primereact/divider';
import { useTranslation } from 'react-i18next';
import MultiSelectPM from '../common/MultiSelectPM/MultiSelectPM';
import ConfirmMessage from '../common/ConfirmMessage/ConfirmMessage';
import SuccessMessage from '../common/SuccessMessage/SuccessMessage';
import DataTablePM from '../common/DataTablePM/DataTablePM';
import { actionTypes, characterLimit, pageTypes } from '../../utils/WebhookProcessManageUtil';
import Loader from '../common/Loader/Loader';
import ErrorMessage from '../common/ErrorMessage/ErrorMessage';
import InputNumberPM from '../common/InputNumberPM/InputNumberPM';
import InputTextAreaPM from '../common/InputTextAreaPM/InputTextAreaPM';
import PropTypes from 'prop-types';
import PrimeDialogComponent from '../common/PrimeDialog/PrimeDialogComponent';

const WebhookProcessManage = ({
  topGridAllColumnNames = null,
  bottomGridAllColumns = null,
  pageType = '',
  loadQueueAPI = null,
  loadDetailsAPI = null,
  performActionAPI = null,
  pageHeader = null,
  topTableHeader = null,
  bottomTableHeader = null,
  bottomTableEmptyMsg = '',
  bottomGridKey = null,
  bottomPaginator = false,
}) => {
  WebhookProcessManage.propTypes = {
    topGridAllColumnNames: PropTypes.array,
    bottomGridAllColumns: PropTypes.array,
    pageType: PropTypes.string,
    loadQueueAPI: PropTypes.string,
    loadDetailsAPI: PropTypes.func,
    performActionAPI: PropTypes.string,
    pageHeader: PropTypes.string,
    topTableHeader: PropTypes.string,
    bottomTableHeader: PropTypes.string,
    bottomTableEmptyMsg: PropTypes.string,
    bottomGridKey: PropTypes.string,
    bottomPaginator: PropTypes.bool,
  };

  const [queueItems, setQueueItems] = useState([]);
  const [queueDetail, setQueueDetail] = useState([]);
  const [sortMeta, setSortMeta] = useState([]);
  const [rowSelections, setRowSelections] = useState([]);
  const [topGridSelectedColumns, setTopGridSelectedColumns] = useState([]);
  const [staticColumnsTopGrid, setStaticColumnsTopGrid] = useState([]);
  const [rowInfo, setRowInfo] = useState([]);
  const [actionType, setActionType] = useState({});
  const [showConfirmMessage, setShowConfirmMessage] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [topTableLoading, setTopTableLoading] = useState(false);
  const [bottomTableLoading, setBottomTableLoading] = useState(false);
  const [ignoreDescription, setIgnoreDescription] = useState(undefined);
  const [retryAfterCounter, setRetryAfterCounter] = useState(0);
  const [reprocessDescription, setReprocessDescription] = useState(undefined);
  const [footerBtnDisable, setFooterBtnDisable] = useState(true);
  const [confirmBtnDisabled, setConfirmBtnDisabled] = useState(false);

  const { t } = useTranslation();

  const tc = (e, data) => (data ? t(`webhooklogqueue:${e}`, data) : t(`webhooklogqueue:${e}`));

  const styles = {
    ignoreLink: { marginLeft: '10px' },
    confirmTextMessageSection: { display: 'flex', paddingTop: '20px' },
    confirmIcon: { fontSize: '1.6em' },
    confirmTextMessage: { marginLeft: '10px' },
    retryAfterLabel: { marginRight: '8px', fontWeight: '400' },
    inputTextAreaSection: { display: 'flex', marginTop: '15px' },
    descriptionLabel: { marginRight: '5px', fontWeight: '400' },
    multiSelectWidth: { width: '39em' },
    errOrderedList: { marginTop: '8px' },
    errMsgSection: {
      display: 'flex',
      paddingTop: '20px',
      flexDirection: 'column',
    },
    descriptionDialog: { width: '50vw' },
  };

  const { reprocess, ignore } = actionTypes;

  useEffect(() => {
    setTopTableLoading(true);
    let isApiSubscribed = true;
    API.get(loadQueueAPI)
      .then(response => {
        if (isApiSubscribed && response.data) {
          setQueueItems(response.data);
        }
      })
      .catch(err => {
        err && err.response && err.response.data && setErrorMessage(err.response.data.status ? err.response.data.status : tc('DEFAULT_ERR_MSG'));
        setShowErrorMessage(true);
      })
      .finally(() => {
        if (isApiSubscribed) {
          setTopTableLoading(false);
        }
      });

    return () => {
      isApiSubscribed = false;
    };
  }, []);

  useEffect(() => {
    if (rowSelections.length === 1) {
      setBottomTableLoading(true);
      API.get(loadDetailsAPI(rowSelections[0].id))
        .then(response => {
          if (response.data) {
            if (pageType === pageTypes.webhookLogQueue) {
              let details = [];
              details.push(response.data);
              setQueueDetail(details);
            } else if (pageType === pageTypes.webhookAcctNotification) {
              setQueueDetail(response.data);
            }
          }
        })
        .catch(err => {
          err && err.response && err.response.data && setErrorMessage(err.response.data.status ? err.response.data.status : tc('DEFAULT_ERR_MSG'));
          setShowErrorMessage(true);
        })
        .finally(() => {
          setBottomTableLoading(false);
        });
    } else {
      setQueueDetail([]);
    }
  }, [rowSelections]);

  useEffect(() => {
    setTopGridSelectedColumns(topGridAllColumnNames);
    const descIndex = topGridAllColumnNames.length;
    const actionIndex = topGridAllColumnNames.length + 1;
    const staticCols = [
      { field: 'description', header: tc('DESCRIPTION_HEADER'), key: { descIndex } },
      { field: 'actions', header: tc('ACTIONS_HEADER'), key: { actionIndex } },
    ];
    setStaticColumnsTopGrid(staticCols);
    setSortMeta([{ field: 'updatedTime', order: -1 }]);
  }, []);

  const actionBodyTemplate = rowData => {
    return (
      <div className="DLQActionCol">
        <a href="#" className="DLQActionLink" onClick={() => onReprocess(rowData)}>
          {tc('REPROCESS_LINK_TEXT')}
        </a>
        <a href="#" style={styles.ignoreLink} className="DLQActionLink" onClick={() => onIgnore(rowData)}>
          {tc('IGNORE_LINK_TEXT')}
        </a>
      </div>
    );
  };
  const onReprocess = rowData => {
    setActionType(reprocess);
    setRowInfo(rowData);
    setRetryAfterCounter(0);
    setReprocessDescription(undefined);
    setShowConfirmMessage(true);
    setConfirmBtnDisabled(true);
  };
  const onIgnore = rowData => {
    setActionType(ignore);
    setRowInfo(rowData);
    setIgnoreDescription(undefined);
    setShowConfirmMessage(true);
    setConfirmBtnDisabled(true);
  };
  const actionTriggerConfirmMsg = () => {
    const { actionName, actionDisplayString } = actionType;

    return (
      <Fragment>
        <div style={styles.confirmTextMessageSection}>
          <i className="pi pi-exclamation-triangle mr-3" style={styles.confirmIcon} />
          {rowInfo && (
            <span style={styles.confirmTextMessage}>
              {rowInfo.length
                ? tc('CONFIRM_MESSAGE_START_TEXT', {
                    actionType: actionDisplayString,
                    rowName: rowInfo.id,
                  })
                : tc('CONFIRM_MESSAGE_MULTIPLE_START_TEXT', {
                    actionType: actionDisplayString,
                  })}
            </span>
          )}
        </div>
        <Divider />
        {actionName === ignore.actionName && (
          <InputTextAreaPM
            value={ignoreDescription}
            placeholder="Reason to Ignore"
            onChange={onChangeTextArea}
            rows={2}
            cols={50}
            maxLength={characterLimit}
            isFooter
            autoResize
          />
        )}
        {actionName === reprocess.actionName && (
          <div>
            <div>
              <label style={styles.retryAfterLabel}>Retry After:</label>
              <InputNumberPM
                inputId="retryAfter"
                value={retryAfterCounter}
                onValueChange={e => setRetryAfterCounter(e.value)}
                mode="decimal"
                suffix="  minutes"
                min={0}
                max={30}
                showButtons
              />
            </div>
            <div style={styles.inputTextAreaSection}>
              <label style={styles.descriptionLabel}>Description:</label>
              <InputTextAreaPM
                value={reprocessDescription}
                placeholder="Reason to Reprocess"
                onChange={onChangeTextArea}
                rows={2}
                cols={50}
                maxLength={characterLimit}
                isFooter
                autoResize
              />
            </div>
          </div>
        )}
      </Fragment>
    );
  };
  const onChangeTextArea = value => {
    const { length } = value.trim();
    if (length > 0) {
      setConfirmBtnDisabled(false);
    } else {
      setConfirmBtnDisabled(true);
    }

    const { actionName } = actionType;
    if (actionName === reprocess.actionName) {
      setReprocessDescription(value);
    } else if (actionName === ignore.actionName) {
      setIgnoreDescription(value);
    }
  };

  const onActionConfirm = () => {
    setLoading(true);
    setShowConfirmMessage(false);

    let selectedIds = [];
    if (rowInfo && rowInfo.length !== 0) {
      selectedIds.push(rowInfo.id);
    } else {
      rowSelections.map(row => selectedIds.push(row.id));
    }

    const { actionName } = actionType;

    API.post(performActionAPI, {
      webhookQueueIds: selectedIds,
      actionType: actionName,
      retryAfterInMinutes: retryAfterCounter,
      reason: actionName === reprocess.actionName ? reprocessDescription : ignoreDescription,
    })
      .then(response => {
        const {
          data: { successful, message, mapOfIdToFailureReason, successfulIds },
        } = response;

        setQueueItems(queueItems.filter(row => successfulIds.indexOf(row.id) === -1));
        let remainingSelectedRows = rowSelections.filter(row => successfulIds.indexOf(row.id) === -1);
        setRowSelections(remainingSelectedRows);
        if (remainingSelectedRows.length === 0) {
          setFooterBtnDisable(true);
        }
        setLoading(false);

        if (successful) {
          if (Object.keys(mapOfIdToFailureReason).length > 0) {
            let errMsg = getActionErrMsg(mapOfIdToFailureReason);
            setErrorMessage(errMsg);
            setShowErrorMessage(true);
          } else {
            if (actionName === reprocess.actionName) {
              setSuccessMessage(tc('REPROCESSED_SUCCESSFULLY'));
            } else if (actionName === ignore.actionName) {
              setSuccessMessage(tc('IGNORED_SUCCESSFULLY'));
            }
            setShowSuccessMessage(true);
          }
        } else {
          setErrorMessage(
            tc('DEFAULT_ERR_MSG', {
              additionalMsg: message,
            })
          );
          setShowErrorMessage(true);
          setLoading(false);
        }
      })
      .catch(err => {
        err && err.response && err.response.data && setErrorMessage(err.response.data.status ? err.response.data.status : tc('DEFAULT_ERR_MSG'));
        setShowErrorMessage(true);
        setLoading(false);
      });
  };
  const getActionErrMsg = mapOfIdToFailureReason => {
    const { actionDisplayString } = actionType;

    return (
      <div style={styles.errMsgSection}>
        <span>{tc('ERROR_MESSAGE_PART_ONE', { actionType: actionDisplayString })}</span>
        <ol style={styles.errOrderedList}>
          {Object.keys(mapOfIdToFailureReason).map(key => {
            return (
              <li key={key}>
                {key} : {mapOfIdToFailureReason[key]}
              </li>
            );
          })}
        </ol>
        <div>{tc('ERROR_MESSAGE_PART_THREE')}</div>
      </div>
    );
  };

  const closeConfirmMessage = (event, reason) => {
    if (reason !== 'escapeKeyDown' && reason !== 'backdropClick') {
      setShowConfirmMessage(false);
    }
  };

  const closeErrorMessage = () => {
    setShowErrorMessage(false);
  };

  const onColumnSort = event => {
    setSortMeta(event.multiSortMeta);
  };

  const onSelectionChange = event => {
    const { value } = event;
    setRowSelections(value);
    if (value.length > 0) {
      setFooterBtnDisable(false);
    } else {
      setFooterBtnDisable(true);
    }
  };

  const onColumnToggle = event => {
    let selectedCols = event.value;
    let orderedSelectedColumns = topGridAllColumnNames.filter(col => selectedCols.some(sCol => sCol.field === col.field));
    setTopGridSelectedColumns(orderedSelectedColumns);
  };
  const columnsInTopGrid = topGridSelectedColumns.map(({ field, header, filterPlaceholder, style }, index) => {
    return (
      <Column
        key={index}
        field={field}
        header={header}
        style={style}
        filter
        filterMatchMode="contains"
        filterPlaceholder={filterPlaceholder}
        sortable
      />
    );
  });

  const descBodyTemplate = rowData => {
    return (
      <a href="#" onClick={() => descPopup(rowData)}>
        {tc('DESCRIPTION_LINK_TEXT')}
      </a>
    );
  };
  const descPopup = rowData => {
    setShowDialog(true);
    setDialogMessage(descriptionMsg(rowData));
  };
  const descriptionMsg = rowData => {
    return (
      <Fragment>
        <div style={styles.confirmTextMessageSection}>
          {rowData.description == null && (
            <span style={styles.confirmTextMessage}>
              {tc('DESCRIPTION_DEFAULT_TEXT', {
                rowId: rowData.id,
              })}
            </span>
          )}

          {rowData.description && (
            <ol style={styles.confirmTextMessage}>
              {rowData.description.split('\r\n').map((text, key) => {
                if (key !== 0 && text.trim().length > 0) {
                  return <li key={key}>{text}</li>;
                }
              })}
            </ol>
          )}
        </div>
      </Fragment>
    );
  };
  const closeDialog = () => {
    setShowDialog(false);
  };
  const descriptionFooter = (
    <div className="modal-button-footer">
      <button className="btn btn-default btn-cancel" onClick={closeDialog}>
        Ok
      </button>
    </div>
  );

  const staticColumns = staticColumnsTopGrid.map(({ key, field, header }, index) => {
    return <Column key={key} field={field} header={header} body={index === 0 ? descBodyTemplate : actionBodyTemplate} />;
  });

  const columnsInBottomGrid = bottomGridAllColumns.map(({ field, header, style }, index) => {
    return <Column key={index} field={field} header={header} style={style} />;
  });

  const headerTemplate = (
    <div className="DLQTableSection">
      <div className="DLQTableHeader">{topTableHeader}</div>
      <div className="DLQTableDropdown">
        <MultiSelectPM
          itemKey="key"
          display="chip"
          value={topGridSelectedColumns}
          options={topGridAllColumnNames}
          optionLabel="header"
          onChange={onColumnToggle}
          inlineStyle={{ width: '39em' }}
        />
      </div>
    </div>
  );

  const onReprocessSel = () => {
    setRowInfo([]);
    setActionType(reprocess);
    setRetryAfterCounter(0);
    setReprocessDescription(undefined);
    setConfirmBtnDisabled(true);
    setShowConfirmMessage(true);
  };
  const onIgnoreSel = () => {
    setRowInfo([]);
    setActionType(ignore);
    setIgnoreDescription(undefined);
    setConfirmBtnDisabled(true);
    setShowConfirmMessage(true);
  };
  const paginatorLeft = (
    <div className="top-grid_buttons">
      <button className="btn btn-sm btn-primary" type="button" onClick={onReprocessSel} disabled={footerBtnDisable}>
        Reprocess Selected
      </button>
      <button className="btn btn-sm btn-primary" style={{ marginLeft: '10px' }} type="button" onClick={onIgnoreSel} disabled={footerBtnDisable}>
        Ignore Selected
      </button>
    </div>
  );

  return (
    <Fragment>
      <div className="DLQ">
        {isLoading ? <Loader /> : ''}
        <div className="DLQContainer">
          <div className="section-header DLQHeader">
            <h1 className="title">
              <i className="icon-dead_letter_queue" /> <span>{pageHeader}</span>
            </h1>
          </div>
          <div className="DLQItems">
            <div role="topTable" className="DLQItemsGrid">
              <DataTablePM
                value={queueItems}
                columns={columnsInTopGrid}
                staticColumns={staticColumns}
                key="id"
                header={headerTemplate}
                loading={topTableLoading}
                showCheckbox={true}
                resizableColumns={true}
                columnResizeMode="expand"
                sortMode="multiple"
                sortMeta={sortMeta}
                handleSort={onColumnSort}
                paginator={true}
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                paginatorLeft={paginatorLeft}
                rows={5}
                rowsPerPageOptions={[5, 15, 45]}
                filterDisplay="rows"
                emptyMessage={tc('NO_ROWS_DEFAULT_TEXT')}
                selection={rowSelections}
                onSelectionChange={onSelectionChange}
                responsiveLayout="scroll"
              />
            </div>
            <div role="bottomTable" className="DLQItemsGrid">
              <DataTablePM
                value={queueDetail}
                columns={columnsInBottomGrid}
                key={bottomGridKey}
                header={bottomTableHeader}
                loading={bottomTableLoading}
                paginator={bottomPaginator}
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                rows={5}
                rowsPerPageOptions={[5, 15, 45]}
                resizableColumns={true}
                columnResizeMode="expand"
                showCheckbox={false}
                emptyMessage={bottomTableEmptyMsg}
                responsiveLayout="scroll"
              />
            </div>
          </div>
        </div>
      </div>

      <PrimeDialogComponent
        visible={showDialog}
        onHide={closeDialog}
        modal={false}
        footer={descriptionFooter}
        header={tc('DESCRIPTION_POPUP_HEADER')}
        maximizable={true}
        content={dialogMessage}
        style={styles.descriptionDialog}
      />

      <ConfirmMessage
        open={showConfirmMessage}
        message={actionTriggerConfirmMsg()}
        onConfirm={onActionConfirm}
        handleClose={closeConfirmMessage}
        confirmBtnDisabled={confirmBtnDisabled}
      />

      <SuccessMessage open={showSuccessMessage} setOpen={setShowSuccessMessage} message={successMessage} type="success" />

      <ErrorMessage open={showErrorMessage} handleClose={closeErrorMessage} message={errorMessage} />
    </Fragment>
  );
};

export default WebhookProcessManage;